import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { AppConfigDisplay } from "../components/app-config-display";
import { GenerateFastStatusChip } from "../components/generate-fast-status-chip";
import { LiveView } from "../components/live-view";
import { ModelNameSelect } from "../components/model-name-select";
import { TestSelect } from "../components/test-select";
import { WebSocketStatusIcon } from "../components/websocket-status-icon";
import { useWebSocketContext } from "../contexts/websocket-context";
import { useApiQuery, usePersonas, useRole, useTypedQueryParams } from "../hooks";
import { useModels } from "../hooks/use-models.ts";
import type { AppConfig } from "../types";
import type {
  TestAgentV2ConfigEvent,
  TestAgentV2Event,
  TestAgentV2IncomingEvent,
  TestAgentV2Status,
  TestAgentV2StatusEvent,
} from "../types/test-agent-2";

interface GenerateFastQuery {
  debug?: string;
}

export const GenerateFastViewer = () => {
  const { messages, send, readyState } = useWebSocketContext<
    TestAgentV2IncomingEvent,
    TestAgentV2Event
  >();

  const nav = useNavigate();

  const { params } = useTypedQueryParams<GenerateFastQuery>();

  const configEvent = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === "config") {
        return messages[i].data as TestAgentV2ConfigEvent;
      }
    }
  }, [messages]);

  const lastStatusEvent = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === "status") {
        return messages[i].data as TestAgentV2StatusEvent;
      }
    }
  }, [messages]);

  const testDependencyId = useMemo(() => {
    return configEvent?.test_dependency_ids?.[0] || null;
  }, [configEvent]);

  const { role } = useRole();

  const { data: modelsData } = useModels();

  const modelName = useMemo(() => {
    return configEvent?.model_name;
  }, [configEvent]);

  const { data: appConfig } = useApiQuery<AppConfig>(`/config/${configEvent?.app_config_id}`, {
    enabled: !!configEvent?.app_config_id,
    refetchInterval: undefined,
  });

  const { data: personas } = usePersonas({
    app_config_id: configEvent?.app_config_id || undefined,
  });

  const selectedPersona = useMemo(() => {
    return personas?.find((p) => p.id === configEvent?.persona_id) || null;
  }, [personas, configEvent?.persona_id]);

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{
        height: "calc(100vh - 64px - 32px)",
        py: 2,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        pt={1}
        sx={{ flexShrink: 0 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <WebSocketStatusIcon readyState={readyState} />
          <GenerateFastStatusChip status={lastStatusEvent?.status || "pending"} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={configEvent?.autopilot ?? false}
                onChange={() => send({ type: "toggle_autopilot" })}
              />
            }
            label={
              <Typography variant="body2" width="80px">
                YOLO Mode
              </Typography>
            }
          />
        </Stack>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <LiveView
          allowInteractive
          liveViewUrl={configEvent?.liveview_url}
          pageUrl={configEvent?.page_url || undefined}
          noLiveViewMessage="No live view available yet, please start the session!"
          defaultHideControls
          onClose={() => nav("/")}
        />
      </Box>
      {appConfig && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            backgroundColor: "background.paper",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <AppConfigDisplay appConfig={appConfig} showChip={false} />
            </Box>
            <Box sx={{ minWidth: 400 }}>
              <TestSelect
                label="Dependency"
                placeholder="Search and select a test..."
                disabled={!!testDependencyId || lastStatusEvent?.status !== "idle"}
                value={testDependencyId}
                onChange={(testId) =>
                  send({ type: "set_test_dependency", id: testId ?? undefined })
                }
                size="small"
                appConfigId={configEvent?.app_config_id ?? undefined}
              />
            </Box>
            <Autocomplete
              size="small"
              options={personas || []}
              value={selectedPersona}
              onChange={(_, value) => {
                send({ type: "set_persona_id", id: value?.id });
              }}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Persona"
                  placeholder="Select persona..."
                  sx={{
                    minWidth: 200,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      fontSize: "0.875rem",
                      fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                        borderWidth: "1.5px",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "8px 12px",
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                      fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                    },
                    "& .MuiFormHelperText-root": {
                      fontSize: "0.75rem",
                      fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        fontSize: "0.875rem",
                        fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                      }}
                    >
                      {option.name}
                    </Typography>
                    {option.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: "0.75rem",
                          fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                        }}
                      >
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />
            <Button
              variant="contained"
              size="small"
              sx={{ px: 3, py: 0.75, fontSize: "0.8rem", fontWeight: 500 }}
              onClick={() => {
                send({ type: "save_test" });
              }}
              disabled={lastStatusEvent?.status !== "idle"}
            >
              Save
            </Button>
          </Stack>
        </Box>
      )}
      {role === "admin" && !!params.debug && (
        <ModelNameSelect
          disabled={!["idle", "pending"].includes(lastStatusEvent?.status as TestAgentV2Status)}
          label="(Admin) Model"
          placeholder="Select model to use..."
          modelName={modelName}
          setModelName={(name) => send({ type: "set_model_name", message: name })}
          models={modelsData?.models ?? []}
        />
      )}
    </Stack>
  );
};
